import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Divider,
} from '@mui/material';
import {Add as AddIcon, Visibility as VisibilityIcon} from '@mui/icons-material';
import {
    createAiTestContainer,
    getAllAiTestContainers,
    getAllReservations
} from "../../api/AiAgentApi";
import {useNavigate} from "react-router-dom";
import {AiTestContainerModelsForm} from "./AiTestContainerModelsForm";

export default function AiTestContainersPage() {
    const navigate = useNavigate();

    const [containers, setContainers] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingReservations, setLoadingReservations] = useState(false);
    const [error, setError] = useState(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        reservationId: '',
        chat: [
            {
                modelName: '',
                modelProvider: 'OPEN_AI',
            },
        ],
    });

    const [viewOpen, setViewOpen] = useState(false);
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    // Словарь резерваций по id для удобного отображения
    const reservationsById = useMemo(() => {
        const map = {};
        reservations.forEach((r) => {
            map[r.id] = r;
        });
        return map;
    }, [reservations]);

    useEffect(() => {
        loadData();
        loadReservations();
    }, []);

    async function loadData() {
        try {
            setLoading(true);
            setError(null);
            const data = await getAllAiTestContainers();
            setContainers(data);
        } catch (e) {
            console.error(e);
            setError('Не удалось загрузить тест-контейнеры');
        } finally {
            setLoading(false);
        }
    }

    async function loadReservations() {
        try {
            setLoadingReservations(true);
            const data = await getAllReservations();
            setReservations(data);
        } catch (e) {
            console.error(e);
            // без жёсткой ошибки, просто не будет списка
        } finally {
            setLoadingReservations(false);
        }
    }

    // ------- Создание контейнера -------

    function handleOpenCreate() {
        setCreateForm({
            reservationId: '',
            chat: [
                {
                    modelName: '',
                    modelProvider: 'OPEN_AI',
                },
            ],
        });
        setCreateOpen(true);
    }

    function handleCloseCreate() {
        setCreateOpen(false);
    }

    function handleChangeReservation(e) {
        setCreateForm((prev) => ({
            ...prev,
            reservationId: e.target.value,
        }));
    }

    function handleChangeModelField(index, field, value) {
        setCreateForm((prev) => {
            const nextChat = [...prev.chat];
            nextChat[index] = {
                ...nextChat[index],
                [field]: value,
            };
            return {...prev, chat: nextChat};
        });
    }

    function handleAddModel() {
        setCreateForm((prev) => ({
            ...prev,
            chat: [
                ...prev.chat,
                {
                    modelName: '',
                    modelProvider: 'OPEN_AI',
                },
            ],
        }));
    }

    function handleRemoveModel(index) {
        setCreateForm((prev) => {
            const nextChat = prev.chat.filter((_, i) => i !== index);
            return {...prev, chat: nextChat.length ? nextChat : prev.chat};
        });
    }

    async function handleSubmitCreate() {
        try {
            if (!createForm.reservationId) {
                alert('Выберите бронирование');
                return;
            }

            const hasEmptyModel = createForm.chat.some(
                (m) => !m.modelName || !m.modelProvider
            );
            if (hasEmptyModel) {
                alert('Заполните все модели');
                return;
            }

            const payload = {
                reservationId: Number(createForm.reservationId),
                chat: createForm.chat,
            };

            await createAiTestContainer(payload);
            handleCloseCreate();
            await loadData();
        } catch (e) {
            console.error(e);
            alert('Ошибка при создании тест-контейнера');
        }
    }

    // ------- Просмотр контейнера -------

    function handleOpenView(id) {
        navigate(`/ai-test-containers/${id}`);
    }

    function handleCloseView() {
        setViewOpen(false);
        setSelectedContainer(null);
    }

// ------- Отрисовка -------

    return (
        <Box p={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight={600}>
                    AI Test Containers
                </Typography>
                <Button variant="contained" startIcon={<AddIcon/>} onClick={handleOpenCreate}>
                    Создать тест-контейнер
                </Button>
            </Stack>

            {loading && (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={24}/>
                    <Typography>Загрузка...</Typography>
                </Stack>
            )}

            {error && (
                <Typography color="error" mb={2}>
                    {error}
                </Typography>
            )}

            {!loading && !error && (
                <Paper>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Reservation</TableCell>
                                <TableCell align="center">Кол-во чатов</TableCell>
                                <TableCell align="right">Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {containers.map((c) => {
                                const reservation = reservationsById[c.reservationId];
                                const reservationLabel = reservation
                                    ? `[${reservation.id}] ${reservation.name || reservation.phone || ''}`
                                    : c.reservationId;

                                return (
                                    <TableRow key={c.id}>
                                        <TableCell>{c.id}</TableCell>
                                        <TableCell>{reservationLabel}</TableCell>
                                        <TableCell align="center">
                                            {Array.isArray(c.chats) ? c.chats.length : 0}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenView(c.id)}
                                                title="Открыть"
                                            >
                                                <VisibilityIcon fontSize="small"/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {containers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography align="center" py={2}>
                                            Пока нет тест-контейнеров
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {/* Диалог создания */}
            <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
                <DialogTitle>Создать AI Test Container</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} mt={1}>
                        <FormControl fullWidth>
                            <InputLabel id="reservation-select-label">
                                Бронирование (reservationId)
                            </InputLabel>
                            <Select
                                labelId="reservation-select-label"
                                label="Бронирование (reservationId)"
                                value={createForm.reservationId}
                                onChange={handleChangeReservation}
                                disabled={loadingReservations}
                            >
                                {reservations.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>
                                        [{r.id}] {r.name || r.phone || 'без имени'} — {r.zone} — {r.persons}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box>
                            <AiTestContainerModelsForm
                                models={createForm.chat}
                                onModelsChange={(next) =>
                                    setCreateForm(prev => ({ ...prev, chat: next }))
                                }
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreate}>Отмена</Button>
                    <Button variant="contained" onClick={handleSubmitCreate}>
                        Создать
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог просмотра контейнера */}
            <Dialog
                open={viewOpen}
                onClose={handleCloseView}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Детали AI Test Container
                </DialogTitle>
                <DialogContent dividers>
                    {viewLoading && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CircularProgress size={24}/>
                            <Typography>Загрузка...</Typography>
                        </Stack>
                    )}

                    {!viewLoading && selectedContainer && (
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                ID: {selectedContainer.id}
                            </Typography>
                            <Typography variant="subtitle2" gutterBottom>
                                ReservationId: {selectedContainer.reservationId}
                            </Typography>

                            <Divider sx={{my: 2}}/>

                            {(!selectedContainer.chats || selectedContainer.chats.length === 0) && (
                                <Typography>Чатов пока нет</Typography>
                            )}

                            <Stack spacing={2}>
                                {selectedContainer.chats?.map((chat) => (
                                    <Paper key={chat.id} variant="outlined" sx={{p: 2}}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Chat #{chat.id}
                                        </Typography>
                                        <Typography variant="body2">
                                            modelName: <b>{chat.modelName}</b>
                                        </Typography>
                                        <Typography variant="body2" mb={1}>
                                            modelProvider: <b>{chat.modelProvider}</b>
                                        </Typography>

                                        <Divider sx={{my: 1}}/>

                                        <Typography variant="subtitle2" gutterBottom>
                                            Messages
                                        </Typography>

                                        {(!chat.messages || chat.messages.length === 0) && (
                                            <Typography variant="body2">
                                                Сообщений пока нет
                                            </Typography>
                                        )}

                                        <Stack spacing={1}>
                                            {chat.messages?.map((m) => (
                                                <Paper
                                                    key={m.id}
                                                    variant="outlined"
                                                    sx={{p: 1.5, bgcolor: m.role === 'BOT' ? 'grey.100' : 'white'}}
                                                >
                                                    <Stack direction="row" justifyContent="space-between">
                                                        <Typography variant="caption">
                                                            #{m.id} · {m.role}
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            {new Date(m.createdAt).toLocaleString()}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="body2" mt={0.5}>
                                                        {m.messageText}
                                                    </Typography>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseView}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
